/**
 * 快速选择（补齐 registry.ts 早先登记的占位工具 / DESIGN.md §5.1）。
 *
 * 本项目目前只有一种"按颜色相似度自动选区"的算法——泛洪
 * （engine/selection/magic-wand.ts，wasm 实现，容差在 Oklab 空间度量），
 * 没有 Photoshop 真正「快速选择」那种沿边缘智能生长的图像分割算法
 * （那是另一套算法，DESIGN.md 附录 A 没有把它列进算法储备）。
 *
 * 如实地在现有算法储备下逼近"快速选择"这个词该有的手感：拖拽笔刷时，
 * 沿途每个新落点都重新泛洪一次并**并入**当前选区——效果是"划过哪片
 * 相近颜色的区域，哪片就被选中"，跟 Photoshop 拖拽画刷、越画选区越大
 * 的感觉一致，只是没有它的边缘感知智能。跟"魔棒"（magic-wand-tool.ts）
 * 共用同一个泛洪算法，区别只在交互：魔棒点一下就定，这里允许连续拖拽。
 * 默认按住即加选（不需要 Shift，这是 PS 快速选择本身的默认行为——属性
 * 栏"选区"按钮组的默认值是"添加"）；Alt 才是减选，Shift+Alt 交叉。
 * 按钮组还能手动切成"新选区"/"交叉"，跟 Shift/Alt 两条路并存
 * （registry.ts 的 `combine` 选项 + marquee.ts 的 `combineFromModifiers`）。
 */
import { magicWand } from '../../engine/selection/magic-wand';
import { feather } from '../../engine/selection/ops';
import { applyCombine, combineFromModifiers, type SelectionCombine } from './marquee';
import { magicWandSampleSource } from './magic-wand-tool';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';

export interface QuickSelectDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
}

export class QuickSelectToolController {
  readonly #deps: QuickSelectDeps;
  #busy = false;
  #dragging = false;

  constructor(deps: QuickSelectDeps) {
    this.#deps = deps;
  }

  /**
   * down 开始拖拽，move 期间沿途继续泛洪并入选区；泛洪是异步 wasm 调用，
   * 上一次没跑完时跳过这一帧的落点——快速划过时自然按完成节奏抽样，
   * 不会因为堆积一串未完成的请求而越攒越慢。
   */
  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    point: { x: number; y: number },
    modifiers: { readonly shiftKey: boolean; readonly altKey: boolean },
  ): boolean {
    const { tools } = this.#deps;
    if (tools.active.id !== 'quickSelect') return false;

    // 属性栏"选区"按钮组记的默认模式（没按修饰键时用哪个）；Shift/Alt
    // 仍然是临时覆盖，每一帧都重新算，笔画中途按下/松开 Alt 照样立刻生效
    // ——跟改之前的行为完全一致，只是多了个可配置的默认值和交叉模式。
    const buttonMode = tools.getOption('quickSelect', 'combine') as SelectionCombine;
    const combine = combineFromModifiers(modifiers, buttonMode);

    if (phase === 'down') {
      this.#dragging = true;
      this.#floodAt(point, combine);
      return true;
    }
    if (phase === 'move') {
      if (this.#dragging) this.#floodAt(point, combine);
      return true;
    }
    if (phase === 'up') {
      this.#dragging = false;
      return true;
    }
    return true;
  }

  #floodAt(point: { x: number; y: number }, combine: SelectionCombine): void {
    const { store, tools } = this.#deps;
    const document = store.document;
    if (document === null || this.#busy) return;

    const seedX = Math.floor(point.x);
    const seedY = Math.floor(point.y);
    if (seedX < 0 || seedY < 0 || seedX >= document.size.width || seedY >= document.size.height) {
      return;
    }

    const tolerance = tools.getOption('quickSelect', 'tolerance') as number;
    const contiguous = tools.getOption('quickSelect', 'contiguous') as boolean;
    const antiAlias = tools.getOption('quickSelect', 'antiAlias') as boolean;
    const sampleAllLayers = tools.getOption('quickSelect', 'sampleAllLayers') as boolean;
    const source = magicWandSampleSource(store, document, sampleAllLayers);

    this.#busy = true;
    void magicWand(source, seedX, seedY, { tolerance, contiguous })
      .then((mask) => {
        const antiAliased = antiAlias ? feather(mask, 1) : mask;
        store.setSelection(applyCombine(store.selection, antiAliased, combine));
      })
      .catch((error: unknown) => {
        store.setHint(`快速选择失败：${error instanceof Error ? error.message : String(error)}`);
      })
      .finally(() => {
        this.#busy = false;
        this.#deps.requestRender();
      });
  }
}
